/**
 * 我的账号：改口令、绑定/关闭二次验证、踢掉全部会话。
 *
 * 绑定 TOTP 是**不可逆的单向门**，所以每一步都要重新验口令 ——
 * 令牌只证明「这个会话曾经登录过」，口令才证明「现在坐在键盘前的是本人」。
 * 少了这一道，拿到一枚有效 access token（XSS / 日志泄漏 / 共享终端）的人
 * 就能 enroll + activate，把受害者永久锁在门外。
 */

import { useState } from 'react';
import { request } from '../lib/api.ts';
import { useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, DetailGrid, ErrorBox, Field, Modal, TextInput,
} from '../components/ui.tsx';
import { ORG_TYPE_LABEL, ROLE_LABEL } from '../lib/format.ts';

interface EnrollResponse {
  readonly secret: string;
  readonly otpauth_uri: string;
  readonly expires_in: number;
}

export function ProfilePage() {
  const session = useSession();
  const { me } = session;
  const [changingPassword, setChangingPassword] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const logoutAll = useMutation(async () =>
    await request<{ ok: boolean; revoked_sessions: number }>('/v1/auth/logout-all', { method: 'POST' }));

  return (
    <>
      <h1>我的账号</h1>

      <Card title="身份">
        <DetailGrid rows={[
          ['邮箱', me.user.email],
          ['用户 id', <CopyId key="u" id={me.user.id} full />],
          ['当前组织', <span key="o">
            {me.memberships.find((m) => m.org_id === me.org.id)?.org_name ?? me.org.id}
          </span>],
          ['组织类型', <Badge key="t" tone="neutral">{ORG_TYPE_LABEL[me.org.type]}</Badge>],
          ['角色', <Badge key="r" tone="neutral">{ROLE_LABEL[me.role]}</Badge>],
          ['组织路径', <span key="p" className="mono" style={{ fontSize: 11.5 }}>{me.org.path}</span>],
          ['二次验证', me.user.mfa_enabled
            ? <Badge key="m" tone="good">已开启</Badge>
            : <Badge key="m" tone="warn">未开启</Badge>],
        ]} />
      </Card>

      <Card title="安全">
        <div className="stack">
          <div className="row">
            <Button onClick={() => { setChangingPassword(true); }}>修改口令</Button>
            {me.user.mfa_enabled
              ? <Button variant="danger" onClick={() => { setDisabling(true); }}>关闭二次验证</Button>
              : <Button variant="primary" onClick={() => { setEnrolling(true); }}>开启二次验证</Button>}
            <Button
              disabled={logoutAll.pending}
              onClick={() => {
                void logoutAll.run().then((result) => { if (result !== null) session.logout(); });
              }}
            >
              退出所有设备
            </Button>
          </div>
          <ErrorBox error={logoutAll.error} />

          {!me.user.mfa_enabled && (
            <div className="alert" data-tone="warn">
              <span className="alert-title">建议开启二次验证</span>
              <span>
                这个账号能看到资金数据、能签发 API 凭证
                {session.can('admin') && '、能审批提现'}。仅靠口令保护是不够的。
              </span>
            </div>
          )}

          <div className="alert" data-tone="info">
            <span className="alert-title">修改口令会作废所有会话</span>
            <span>包含攻击者可能正持有的那一条。改完需要重新登录。</span>
          </div>
        </div>
      </Card>

      {changingPassword && (
        <ChangePasswordModal
          onClose={() => { setChangingPassword(false); }}
          onDone={() => { setChangingPassword(false); session.logout(); }}
        />
      )}
      {enrolling && (
        <EnrollTotpModal
          onClose={() => { setEnrolling(false); }}
          onDone={() => { setEnrolling(false); session.reload(); }}
        />
      )}
      {disabling && (
        <DisableTotpModal
          onClose={() => { setDisabling(false); }}
          onDone={() => { setDisabling(false); session.reload(); }}
        />
      )}
    </>
  );
}

function ChangePasswordModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation(async () => await request('/v1/auth/password', {
    method: 'POST',
    body: { current_password: current, new_password: next },
  }));

  const mismatch = confirm !== '' && next !== confirm;

  return (
    <Modal
      title="修改口令"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={change.pending || current === '' || next.length < 12 || mismatch || confirm === ''}
            onClick={() => { void change.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {change.pending ? '提交中…' : '修改'}
          </Button>
        </>
      }
    >
      <ErrorBox error={change.error} />
      <Field label="当前口令">
        <TextInput value={current} onChange={setCurrent} type="password" />
      </Field>
      <Field label="新口令" hint="至少 12 位。">
        <TextInput value={next} onChange={setNext} type="password" />
      </Field>
      <Field label="确认新口令" {...(mismatch ? { hint: '两次输入不一致' } : {})}>
        <TextInput value={confirm} onChange={setConfirm} type="password" />
      </Field>
      <div className="alert" data-tone="info">
        <span className="alert-title">改完会立刻退出登录</span>
        <span>所有设备上的会话都会作废，需要用新口令重新登录。</span>
      </div>
    </Modal>
  );
}

function EnrollTotpModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrolled, setEnrolled] = useState<EnrollResponse | null>(null);

  const enroll = useMutation(async () => await request<EnrollResponse>('/v1/auth/totp/enroll', {
    method: 'POST',
    body: { password },
  }));

  const activate = useMutation(async () => await request('/v1/auth/totp/activate', {
    method: 'POST',
    body: { password, code },
  }));

  return (
    <Modal
      title="开启二次验证"
      onClose={onClose}
      footer={
        enrolled === null ? (
          <>
            <Button onClick={onClose}>取消</Button>
            <Button
              variant="primary"
              disabled={enroll.pending || password === ''}
              onClick={() => {
                void enroll.run().then((result) => { if (result !== null) setEnrolled(result); });
              }}
            >
              {enroll.pending ? '生成中…' : '下一步'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>取消</Button>
            <Button
              variant="primary"
              disabled={activate.pending || code.length < 6}
              onClick={() => { void activate.run().then((result) => { if (result !== null) onDone(); }); }}
            >
              {activate.pending ? '激活中…' : '激活'}
            </Button>
          </>
        )
      }
    >
      <ErrorBox error={enroll.error} />
      <ErrorBox error={activate.error} />

      {enrolled === null ? (
        <>
          <Field
            label="当前口令"
            hint="绑定二次验证需要重新验口令 —— 令牌只证明这个会话曾经登录过，口令才证明现在是本人。"
          >
            <TextInput value={password} onChange={setPassword} type="password" />
          </Field>
        </>
      ) : (
        <>
          <div className="alert" data-tone="warn">
            <span className="alert-title">先把密钥存好，再激活</span>
            <span>
              激活之后每次登录都需要验证码。如果丢了身份验证器又没存密钥，
              就只能找上级管理员帮你重置。
            </span>
          </div>

          <Field label="密钥（手动录入用）">
            <div className="secret-box">{enrolled.secret}</div>
          </Field>

          <Field label="otpauth URI（可生成二维码）">
            <div className="secret-box">{enrolled.otpauth_uri}</div>
          </Field>

          <Field label="输入当前验证码以完成激活" hint={`密钥 ${enrolled.expires_in} 秒后失效，超时需要重新开始。`}>
            <TextInput value={code} onChange={setCode} placeholder="000000" />
          </Field>
        </>
      )}
    </Modal>
  );
}

function DisableTotpModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const disable = useMutation(async () => await request('/v1/auth/totp', {
    method: 'DELETE',
    body: { password, code },
  }));

  return (
    <Modal
      title="关闭二次验证"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="danger"
            disabled={disable.pending || password === '' || code.length < 6}
            onClick={() => { void disable.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {disable.pending ? '关闭中…' : '关闭'}
          </Button>
        </>
      }
    >
      <ErrorBox error={disable.error} />
      <Field label="当前口令">
        <TextInput value={password} onChange={setPassword} type="password" />
      </Field>
      <Field label="当前验证码" hint="口令和验证码都要 —— 只有真正持有种子的人才能关掉它。">
        <TextInput value={code} onChange={setCode} placeholder="000000" />
      </Field>
    </Modal>
  );
}
